import { useState, useEffect, Fragment } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { NavLink } from "react-router-dom";
import FleetWebApi from "../Api/FleetWebApi";
import SignalCellular0BarIcon from "@mui/icons-material/SignalCellular0Bar";
import SignalCellular1BarIcon from "@mui/icons-material/SignalCellular1Bar";
import SignalCellular2BarIcon from "@mui/icons-material/SignalCellular2Bar";
import SignalCellular3BarIcon from "@mui/icons-material/SignalCellular3Bar";
import SignalCellular4BarIcon from "@mui/icons-material/SignalCellular4Bar";

function RenderArrayItems(arr) {
  return Object.keys(arr).map((idx) => <li key={idx}>{arr[idx]}</li>);
}

function scoreColor(score) {
  if (score === "Good") return "#4CAF50";
  if (score === "Okay") return "#FFC107";
  if (score === "Bad") return "#FF7043";
  if (score === "Very Bad") return "#F44336";
  return "inherit";
}

function CellBarsIcon({ value }) {
  const bars = Math.round(value);
  const color = bars <= 1 ? "error" : bars <= 2 ? "warning" : "success";
  if (bars === 0) return <SignalCellular0BarIcon color={color} fontSize="small" />;
  if (bars === 1) return <SignalCellular1BarIcon color={color} fontSize="small" />;
  if (bars === 2) return <SignalCellular2BarIcon color={color} fontSize="small" />;
  if (bars === 3) return <SignalCellular3BarIcon color={color} fontSize="small" />;
  return <SignalCellular4BarIcon color={color} fontSize="small" />;
}

function formatUptime(seconds) {
  if (!seconds) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function Metric({ label, value, score, children }) {
  return value != null ? (
    <li style={{ marginBottom: 4, color: score ? scoreColor(score) : "inherit" }}>
      {label}:{" "}
      {children ?? value}
    </li>
  ) : null;
}

function CompanionConnection(props) {
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState();
  const [connectionData, setConnectionData] = useState();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setConnectionData(null);
    setError(null);

    FleetWebApi.getCompanionConnectionSummary(
      props.coreId,
      props.startDate,
      props.endDate
    )
      .then((resp) => {
        if (isMounted) {
          setConnectionData(resp);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [props.coreId, props.startDate, props.endDate]);

  if (isLoading) {
    return <CircularProgress />;
  }

  const d = connectionData;

  return (
    <Container maxWidth="lg" className="container">
      <h3>Connection Summary</h3>
      {error ? (
        <p style={{ color: "#FFC107" }}>No connection data available for selected range.</p>
      ) : !d ? null : (
        <Grid container spacing={3}>
          {/* Col 1 — problems / status */}
          <Grid item xs={12} md={4}>
            <h4>Detected Problems</h4>
            {d.problems && d.problems.length > 0 ? (
              <Fragment>
                <ul style={{ color: "#F44336" }}>
                  {RenderArrayItems(d.problems)}
                </ul>
              </Fragment>
            ) : (
              <ul style={{ color: "#4CAF50" }}>No problems detected</ul>
            )}
          </Grid>

          {/* Col 2 — signal & connectivity */}
          <Grid item xs={12} md={4}>
            <h4>Signal & Connectivity</h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <Metric
                label="Avg LoRa Bars"
                value={d.lora_bars?.value}
                score={d.lora_bars?.score}
              >
                {d.lora_bars?.value != null && `${d.lora_bars.value.toFixed(1)}`}
              </Metric>

              <Metric
                label="Avg Cell Bars (companion)"
                value={d.cellular_bars_from_companion?.value}
                score={d.cellular_bars_from_companion?.score}
              >
                {d.cellular_bars_from_companion?.value != null && (
                  <span style={{ verticalAlign: "middle" }}>
                    {d.cellular_bars_from_companion.value.toFixed(1)}{" "}
                    <CellBarsIcon value={d.cellular_bars_from_companion.value} />
                  </span>
                )}
              </Metric>

              <Metric
                label="Hub Cloud Online"
                value={d.proxy_online?.value}
                score={d.proxy_online?.score}
              >
                {d.proxy_online?.value != null && `${d.proxy_online.value.toFixed(1)}%`}
              </Metric>

              <Metric
                label="Avg Packet Success"
                value={d.avg_packet_success?.value}
                score={d.avg_packet_success?.score}
              >
                {d.avg_packet_success?.value != null && `${d.avg_packet_success.value.toFixed(1)}%`}
              </Metric>

              <Metric
                label="Hub Uptime"
                value={d.proxy_uptime?.value}
                score={d.proxy_uptime?.score}
              >
                {d.proxy_uptime?.value != null && formatUptime(d.proxy_uptime.value)}
              </Metric>
            </ul>
          </Grid>

          {/* Col 3 — device info */}
          <Grid item xs={12} md={4}>
            <h4>Device Info</h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <Metric
                label="Companion Reboots"
                value={d.num_reboots?.value}
                score={d.num_reboots?.score}
              />

              <Metric
                label="Hub Switches"
                value={d.proxy_switches?.value}
                score={d.proxy_switches?.score}
              />

              {d.latest_proxy && (
                <li style={{ marginBottom: 4 }}>
                  Current Hub:{" "}
                  <NavLink
                    to={`/device/${d.latest_proxy}`}
                    style={{ fontFamily: "monospace", fontSize: "0.85em", color: "#1976d2" }}
                  >
                    {d.latest_proxy}
                  </NavLink>
                </li>
              )}
            </ul>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default CompanionConnection;
